/**
 * Página /tenant/settings/exports — Exportação Dados (auditoria)
 * -----------------------------------------------------------------------------
 * Reúne, em um só lugar, tudo que foi exportado do sistema: quem solicitou,
 * qual módulo, formato, forma de entrega, quantidade de registros, situação e
 * os filtros/colunas usados na exportação.
 *
 * Os avisos de exportação NÃO têm configuração própria: reaproveitam o centro
 * de notificações existente. Por isso a página apenas oferece um atalho para
 * as Preferências de Notificação do usuário (módulo "Exportações").
 *
 * Layout: shell ListPageLayout (padrão obrigatório de listagens).
 *
 * @version 1.1.0
 * RELEASE: 2026-09-07 v1.1.0 - Detalhes passam para o painel lateral
 *          ExportLogDetailSheet (padrão dos sheets de Indicação) e o cartão
 *          "Registros exportados" sai dos indicadores (somava números de
 *          recortes diferentes, o que induzia leitura errada).
 * RELEASE: 2026-09-07 v1.0.0 - Criação inicial.
 */

import { useMemo, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import {
  calculatePeriodDates,
  type PeriodFilter,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
import {
  FileDown,
  Search,
  RefreshCw,
  Mail,
  AlertTriangle,
  Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useExportLogs, type ExportLogRow } from '@/hooks/useExportLogs';
import { EXPORT_SOURCE_LIST, EXPORT_SOURCES } from '@/lib/export/exportRegistry';
import { ExportLogDetailSheet } from '@/components/export/ExportLogDetailSheet';
import { NotificationPreferencesSheet } from '@/components/tenant/notifications/NotificationPreferencesSheet';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

/** Rótulo amigável de entrega */
const ROTULO_ENTREGA: Record<string, string> = {
  download: 'Download',
  email: 'E-mail',
};

/** Rótulo amigável de situação + cor da bolinha */
const META_STATUS: Record<string, { label: string; dot: string }> = {
  success: { label: 'Concluída', dot: 'bg-emerald-500' },
  error: { label: 'Com erro', dot: 'bg-destructive' },
  processing: { label: 'Processando', dot: 'bg-amber-500' },
};

/** Formata data/hora no padrão brasileiro */
function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DataExports() {
  // ---------------------------------------------------------------- Filtros
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [exportKey, setExportKey] = useState<string>('__all');
  const [formato, setFormato] = useState<string>('__all');
  const [entrega, setEntrega] = useState<string>('__all');
  const [status, setStatus] = useState<string>('__all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Período: só envia recorte de datas quando o usuário interage
  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const { startDate, endDate } = calculatePeriodDates('month');
    return { preset: 'month', startDate, endDate };
  });
  const [periodAtivo, setPeriodAtivo] = useState(false);

  // Modais
  const [registroSelecionado, setRegistroSelecionado] = useState<ExportLogRow | null>(null);
  const [prefsAberta, setPrefsAberta] = useState(false);

  const filtros = useMemo(
    () => ({
      page,
      pageSize,
      dateFrom: periodAtivo ? period.startDate.toISOString() : null,
      dateTo: periodAtivo ? period.endDate.toISOString() : null,
      userId: null,
      exportKey: exportKey === '__all' ? null : exportKey,
      format: formato === '__all' ? null : formato,
      delivery: entrega === '__all' ? null : entrega,
      status: status === '__all' ? null : status,
      search: search || null,
    }),
    [page, pageSize, periodAtivo, period, exportKey, formato, entrega, status, search],
  );

  const q = useExportLogs(filtros);
  const linhas = q.data ?? [];
  const total = Number(linhas[0]?.total_count ?? 0);

  // Indicadores vêm da própria RPC (mesmo recorte de filtros)
  const kpis = {
    total: Number(linhas[0]?.kpi_total ?? 0),
    email: Number(linhas[0]?.kpi_email ?? 0),
    erros: Number(linhas[0]?.kpi_error ?? 0),
  };

  const handlePresetChange = (preset: PeriodPreset) => {
    if (preset === 'custom') return;
    const { startDate, endDate } = calculatePeriodDates(preset);
    setPeriod({ preset, startDate, endDate });
    setPeriodAtivo(true);
    setPage(1);
  };
  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setPeriod({ preset: 'custom', startDate, endDate });
    setPeriodAtivo(true);
    setPage(1);
  };

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Exportação Dados"
        description="Auditoria de todos os arquivos exportados do sistema: quem solicitou, filtros usados, formato e entrega."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPrefsAberta(true)}>
              <Bell className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Avisos de exportação</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => q.refetch()}
              disabled={q.isFetching}
            >
              <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} />
            </Button>
          </div>
        }
      />

      <ListPageKpis columns={3}>
        <KpiCard
          color="purple"
          icon={FileDown}
          label="Exportações"
          value={q.isLoading ? '…' : kpis.total}
        />
        <KpiCard
          color="green"
          icon={Mail}
          label="Enviadas por e-mail"
          value={q.isLoading ? '…' : kpis.email}
        />
        <KpiCard
          color="orange"
          icon={AlertTriangle}
          label="Com erro"
          value={q.isLoading ? '…' : kpis.erros}
        />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={q.isFetching}>
        <div className="relative flex-1 min-w-[200px] sm:flex-none sm:w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, 'pl-7 sm:pl-8')}
            placeholder="Buscar por quem solicitou…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearch(searchInput);
                setPage(1);
              }
            }}
            onBlur={() => {
              if (searchInput !== search) {
                setSearch(searchInput);
                setPage(1);
              }
            }}
          />
        </div>

        <Select
          value={exportKey}
          onValueChange={(v) => {
            setExportKey(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[170px]')}>
            <SelectValue placeholder="Módulo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos módulos</SelectItem>
            {EXPORT_SOURCE_LIST.map((fonte) => (
              <SelectItem key={fonte.id} value={fonte.id}>
                {fonte.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={formato}
          onValueChange={(v) => {
            setFormato(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[120px]')}>
            <SelectValue placeholder="Formato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Formatos</SelectItem>
            <SelectItem value="csv">CSV</SelectItem>
            <SelectItem value="xls">XLSX</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={entrega}
          onValueChange={(v) => {
            setEntrega(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[130px]')}>
            <SelectValue placeholder="Entrega" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Entregas</SelectItem>
            <SelectItem value="download">Download</SelectItem>
            <SelectItem value="email">E-mail</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[130px]')}>
            <SelectValue placeholder="Situação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Situações</SelectItem>
            <SelectItem value="success">Concluída</SelectItem>
            <SelectItem value="error">Com erro</SelectItem>
          </SelectContent>
        </Select>

        <CrmPeriodFilter
          value={period}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
        />
      </ListPageFiltersBar>

      <ListPageContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Data</TableHead>
              <TableHead>Solicitante</TableHead>
              <TableHead>Módulo</TableHead>
              <TableHead className="w-[90px]">Formato</TableHead>
              <TableHead className="w-[180px]">Entrega</TableHead>
              <TableHead className="w-[110px] text-right">Registros</TableHead>
              <TableHead className="w-[140px]">Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                  Carregando exportações…
                </TableCell>
              </TableRow>
            )}

            {!q.isLoading && linhas.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                  Nenhuma exportação encontrada para os filtros selecionados.
                </TableCell>
              </TableRow>
            )}

            {linhas.map((linha) => {
              const meta = META_STATUS[linha.status] ?? {
                label: linha.status,
                dot: 'bg-muted-foreground',
              };
              const modulo = EXPORT_SOURCES[linha.export_key]?.label ?? linha.export_key;

              return (
                <TableRow
                  key={linha.id}
                  className="cursor-pointer"
                  onClick={() => setRegistroSelecionado(linha)}
                >
                  <TableCell className="text-sm whitespace-nowrap">
                    {formatarDataHora(linha.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-0">
                      <SmartAvatar
                        src={linha.user_avatar_url}
                        name={linha.user_name}
                        email={linha.user_email}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <p className="text-sm truncate">{linha.user_name}</p>
                        {linha.user_email && (
                          <p className="text-xs text-muted-foreground truncate">
                            {linha.user_email}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{modulo}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-normal uppercase">
                      {linha.format === 'xls' ? 'XLSX' : linha.format}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span>{ROTULO_ENTREGA[linha.delivery] ?? linha.delivery}</span>
                    {linha.recipient_email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {linha.recipient_email}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-right">{linha.row_count ?? 0}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2 text-sm">
                      <span className={cn('h-2 w-2 rounded-full shrink-0', meta.dot)} />
                      <span className="text-foreground">{meta.label}</span>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(tamanho) => {
            setPageSize(tamanho);
            setPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

      {/* Detalhes: painel lateral com filtros legíveis e colunas usadas */}
      <ExportLogDetailSheet
        open={!!registroSelecionado}
        onOpenChange={(aberto) => !aberto && setRegistroSelecionado(null)}
        registro={registroSelecionado}
      />

      {/* Avisos de exportação: centro de notificações já existente */}
      <NotificationPreferencesSheet open={prefsAberta} onOpenChange={setPrefsAberta} />
    </ListPageLayout>
  );
}
